import { Platform, Pressable, StyleSheet, View, useWindowDimensions } from "react-native";
import { useTheme } from "../../../../hooks/use-theme";
import LogoImage from "../../../../assets/images/logo.svg";
import {
    AUTOMATIC_SLIDESHOW_TRANSITION,
    BUTTON_BORDER_RADIUS,
    ButtonPosition,
    CAROUSEL_BOTTOM_HEIGHT_BASE,
    LARGE_CAROUSEL_BORDER_WIDTH,
    LARGE_CAROUSEL_HEIGHT,
    LARGE_CAROUSEL_WIDTH,
    LargeWelcomeCarousel,
} from "../../../../components/LargeWelcomeCarousel";
import VStack from "../../../../ui_atoms/VStack";
import auth from "../../../../contexts/FirebaseAuth";
import QRCode from "react-qr-code";
import Text from "../../../../components/Text";
import MarkdownView from "../../../../components/MarkdownView";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useI18n } from "../../../../hooks/i18n-hooks";
import Constants from "expo-constants";
import Button from "../../../../components/Button";
import PhoneField, { PhoneFieldHandle } from "../../../../components/PhoneField";
import { WelcomeScreenProps } from "../Welcome";
import { AuthManagerContext } from "../../../../contexts/AuthManager";
import commonStyle from "../../../common-style";
import CodeField from "../../../../components/CodeField";
import { useLazyQuery } from "@apollo/client";
import { GET_DEFAULT_PROFILE_TYPE } from "../../../../graphql/identity";
import { CELL_COUNT } from "../../ConfirmationCode";
import Loading from "../../../../components/Loading";
import { FirebaseAuthTypes } from "@react-native-firebase/auth";
import { ProfileType } from "../../../../__generated__/graphql";
import { FirebaseManagerContext } from "../../../../contexts/FirebaseManager";
import { RandomLine, RandomLineFunctions } from "../../../../components/RandomLine";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import BlurView from "../../../../components/BlurView";
import { LinearGradient } from "expo-linear-gradient";
import { BodySmallText } from "../../../../components/FormattedText";
import AutoSizeImage from "../../../../components/AutoSizeImage";
import MaskedView, { MaskedViewFunctions } from "@react-native-masked-view/masked-view";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import Icon from "../../../../components/Icon";
import { useFadeAnimation } from "../../../../hooks/useFadeAnimation";

const AnimatedMarkdownView = Animated.createAnimatedComponent(MarkdownView);
const AnimatedPressable = Animated.createAnimatedComponent(Pressable);
const AnimatedIcon = Animated.createAnimatedComponent(Icon);
const AnimatedButton = Animated.createAnimatedComponent(Button);

const PARTNER_LOGO_HEIGHT = 28;

type MessageInfo = {
    text: string;
    color?: string;
};

const captchaDisabledPhones = (
    (Constants.expoConfig?.extra?.captchaDisabledPhones as string) || ""
).split(",");

const CAPTCHA_ID = uuidv4();

export const LargeLayout = ({ navigation, route }: WelcomeScreenProps) => {
    const { isLogin: _isInitialLogin = "false" } = route.params || {};
    const isInitialLogin = _isInitialLogin.toLowerCase() === "true";
    const [isLogin, setIsLogin] = useState(isInitialLogin);
    const styles = useStyles();
    const { colors, gridSize } = useTheme();
    const i18n = useI18n();
    const phoneInputRef = useRef<PhoneFieldHandle>(null);
    const [message, setMessage] = useState<MessageInfo>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [codeValue, setCodeValue] = useState("");
    const [phoneNumberConfirmResultValue, setPhoneNumberConfirmResult] =
        useState<FirebaseAuthTypes.ConfirmationResult>();
    const { user, signInWithPhoneNumber } = useContext(AuthManagerContext);
    const { user: firebaseUser } = useContext(FirebaseManagerContext);

    // Phone number part

    const isPhoneNumberValid = useMemo(() => {
        return (
            (phoneNumber?.length || 0) > 0 && !!phoneInputRef.current?.isValidNumber(phoneNumber)
        );
    }, [phoneInputRef, phoneNumber]);

    const validatePhoneNumber = useCallback(async () => {
        try {
            setIsProcessing(true);
            const phoneValid = phoneInputRef.current?.isValidNumber(phoneNumber);
            if (!phoneValid) {
                setMessage({
                    text: i18n.t("screens.auth.phone_number.invalid_number"),
                    color: "red",
                });
                return;
            }

            const formattedPhoneNumber = phoneInputRef.current?.formatPhoneNumber();

            if (formattedPhoneNumber) {
                if (Platform.OS === "web") {
                    if (captchaDisabledPhones.includes(formattedPhoneNumber)) {
                        auth().settings.appVerificationDisabledForTesting = true;
                    }

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    if (!window.verifier) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                        window.verifier = new auth.RecaptchaVerifier(CAPTCHA_ID, {
                            size: "invisible",
                        });
                    }
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    window.verifier.verify();
                }

                const confirmation = await signInWithPhoneNumber(phoneNumber);
                setPhoneNumberConfirmResult(confirmation);
            }
        } catch (err) {
            let errorMessage: string;
            if (err instanceof Error) {
                errorMessage = err.message;
            } else {
                errorMessage = String(err);
            }
            setMessage({ text: `Error: ${errorMessage}`, color: "red" });
        } finally {
            setIsProcessing(false);
        }
    }, [phoneNumber, i18n, signInWithPhoneNumber]);

    const onPhoneNumberChanged = useCallback((text: string) => {
        setPhoneNumber(text);
        setMessage(undefined);
    }, []);

    // CodeFieldProps

    const [getDefaultProfileType, { loading: getDefaultProfileTypeLoading }] =
        useLazyQuery(GET_DEFAULT_PROFILE_TYPE);

    const isCodeValid = useMemo(() => {
        return !!phoneNumberConfirmResultValue && codeValue.length >= CELL_COUNT;
    }, [codeValue, phoneNumberConfirmResultValue]);

    const isLoading = isProcessing || getDefaultProfileTypeLoading;

    const validateCode = useCallback(async () => {
        try {
            setMessage(undefined);
            setIsProcessing(true);

            await phoneNumberConfirmResultValue?.confirm(codeValue);
            // signin will change the auth user
            // if user exists and is a worker the root navigations controller that will transition to the User stack
            // in all other cases, a hook will do the navigation to next screen
        } catch (err) {
            setMessage({
                text: i18n.t("screens.auth.confirmation_code.wrong_code"),
                color: "red",
            });
            setIsProcessing(false);
        }
    }, [phoneNumberConfirmResultValue, codeValue, i18n]);

    useEffect(() => {
        // once user === null it means we have login to firebase (user has entered its code) and don't have an account yet
        if (firebaseUser && user === null) {
            void (async () => {
                const { data: { getDefaultProfileType: profileType } = {} } =
                    await getDefaultProfileType();

                if (profileType === ProfileType.Client) {
                    navigation.replace("SignupSigninStack", {
                        initialRouteName: "CheckClientInfo",
                        isSignup: !isLogin,
                    });
                } else if (profileType === ProfileType.Worker) {
                    navigation.replace("SignupSigninStack", {
                        initialRouteName: "CheckWorkerInfo",
                        isSignup: !isLogin,
                    });
                } else {
                    navigation.replace("SignupSigninStack", {
                        initialRouteName: "SelectRole",
                        isSignup: !isLogin,
                    });
                }
            })();
        }
    }, [firebaseUser, user, navigation, getDefaultProfileType, isLogin]);

    useEffect(() => {
        if (isCodeValid) {
            void validateCode();
        }
    }, [isCodeValid, validateCode]);

    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const insets = useSafeAreaInsets();

    const backgroundLineRef = useRef<RandomLineFunctions>(null);
    const titleOpacity = useSharedValue(1);

    const opacityTitleStyles = useAnimatedStyle(() => ({
        opacity: titleOpacity.value,
    }));

    const [title, setTitle] = useState(
        `## ${i18n.t("screens.auth.presentation_carousel.step_0.title")}`,
    );

    const maskRef = useRef<MaskedViewFunctions>(null);

    const loginTexts = useMemo(
        () => [
            {
                topRight: i18n.t("screens.auth.presentation_carousel.already_have_account"),
                title: i18n.t("screens.auth.welcome.start_new_adventure"),
                description: i18n.t("screens.auth.phone_number.description_web"),
                button: i18n.t("screens.auth.welcome.signup"),
            },
            {
                topRight: i18n.t("screens.auth.presentation_carousel.no_account"),
                title: i18n.t("screens.auth.welcome.connect"),
                description: i18n.t("screens.auth.phone_number.description"),
                button: i18n.t("login"),
            },
        ],
        [i18n],
    );

    const [topRightText, setTopRightText, topRightStyle] = useFadeAnimation({
        initialText: loginTexts[+isLogin].topRight,
    });
    const [titleText, setTitleText, titleStyle] = useFadeAnimation({
        initialText: loginTexts[+isLogin].title,
    });
    const [descriptionText, setDescriptionText, descriptionStyle] = useFadeAnimation({
        initialText: loginTexts[+isLogin].description,
    });
    const [buttonText, setButtonText, buttonStyle] = useFadeAnimation({
        initialText: loginTexts[+isLogin].button,
    });

    const [buttonPosition, setButtonPosition] = useState<ButtonPosition>();
    const [isPanelFullScreen, setIsPanelFullScreen] = useState(isLogin);
    const [isButtonPositionLoading, setIsButtonPositionLoading] = useState(isLogin);

    const carouselScale = useMemo(() => {
        if (windowHeight > 940) {
            return 1;
        } else if (windowHeight > 880) {
            return 0.9;
        } else if (windowHeight > 820) {
            return 0.8;
        } else {
            return 0.65;
        }
    }, [windowHeight]);

    const panelValues = useMemo(
        () => ({
            rightMarginLeft: [windowWidth / 2, 0],
            rightWidth: [windowWidth / 2, windowWidth],
            leftOpacity: [1, 0],
            buttonTopRadius: [0, BUTTON_BORDER_RADIUS * carouselScale],
            buttonBackgroundColor: ["rgba(255, 255, 255, 0)", "rgba(255, 255, 255, 1)"],
            rightPaddingLeft: [gridSize * 4, buttonPosition?.x || 0],
            buttonContentOpacity: [0, 1],
        }),
        [windowWidth, carouselScale, gridSize, buttonPosition?.x],
    );

    const leftOpacity = useSharedValue(panelValues.rightMarginLeft[+isLogin]);
    const buttonTopRadius = useSharedValue(panelValues.buttonTopRadius[+isLogin]);
    const rightMarginLeft = useSharedValue(panelValues.rightMarginLeft[+isLogin]);
    const rightWidth = useSharedValue(panelValues.rightWidth[+isLogin]);
    const rightPaddingLeft = useSharedValue(panelValues.rightPaddingLeft[+isLogin]);
    const buttonBackgroundColor = useSharedValue(panelValues.buttonBackgroundColor[+isLogin]);
    const buttonContentOpacity = useSharedValue(panelValues.buttonContentOpacity[+isLogin]);

    const onSwitchPanel = useCallback(
        (isOpen: boolean, duration = AUTOMATIC_SLIDESHOW_TRANSITION) => {
            rightMarginLeft.value = withTiming(panelValues.rightMarginLeft[+isOpen], {
                duration: duration,
            });
            rightWidth.value = withTiming(panelValues.rightWidth[+isOpen], {
                duration: duration,
            });
            leftOpacity.value = withTiming(panelValues.leftOpacity[+isOpen], {
                duration: duration,
            });
            buttonTopRadius.value = withTiming(panelValues.buttonTopRadius[+isOpen], {
                duration: duration,
            });
            buttonBackgroundColor.value = withTiming(panelValues.buttonBackgroundColor[+isOpen], {
                duration: duration / 2,
            });
            rightPaddingLeft.value = withTiming(panelValues.rightPaddingLeft[+isOpen], {
                duration: duration,
            });
            buttonContentOpacity.value = withTiming(
                panelValues.buttonContentOpacity[+isOpen],
                {
                    duration: duration,
                },
                (finished) => {
                    if (finished) {
                        setIsPanelFullScreen(isOpen);
                        setTimeout(() => {
                            setIsButtonPositionLoading(false);
                        }, 1);
                    }
                },
            );
        },
        [
            buttonBackgroundColor,
            buttonContentOpacity,
            buttonTopRadius,
            leftOpacity,
            panelValues.buttonBackgroundColor,
            panelValues.buttonContentOpacity,
            panelValues.buttonTopRadius,
            panelValues.leftOpacity,
            panelValues.rightMarginLeft,
            panelValues.rightPaddingLeft,
            panelValues.rightWidth,
            rightMarginLeft,
            rightPaddingLeft,
            rightWidth,
        ],
    );

    const onCarouselChange = useCallback(
        (toIndex: number | "full") => {
            backgroundLineRef.current?.randomize();
            titleOpacity.value = withTiming(
                0,
                { duration: AUTOMATIC_SLIDESHOW_TRANSITION / 2 },
                (finished) => {
                    if (finished) {
                        if (toIndex === 0) {
                            setTitle(
                                `## ${i18n.t(`screens.auth.presentation_carousel.step_${toIndex}.title`)}`,
                            );
                        } else if (typeof toIndex === "number") {
                            setTitle(
                                i18n.t(
                                    `screens.auth.presentation_carousel.step_${toIndex + 1}.title`,
                                ),
                            );
                        }
                        titleOpacity.value = withTiming(1, {
                            duration: AUTOMATIC_SLIDESHOW_TRANSITION / 2,
                        });
                    }
                },
            );
            if (toIndex === "full") {
                onSwitchPanel(true);
            }
        },
        [titleOpacity, i18n, onSwitchPanel],
    );

    const leftStyles = useAnimatedStyle(() => ({
        opacity: leftOpacity.value,
    }));

    useEffect(() => {
        buttonTopRadius.addListener(0, () => {
            maskRef.current?.updateMask();
        });
        return () => {
            buttonTopRadius.removeListener(0);
        };
    }, [buttonTopRadius]);

    const buttonStyles = useAnimatedStyle(() => ({
        borderTopLeftRadius: buttonTopRadius.value,
        borderTopRightRadius: buttonTopRadius.value,
    }));

    const buttonContentStyles = useAnimatedStyle(() => ({
        opacity: buttonContentOpacity.value,
    }));

    const buttonBackgroundStyles = useAnimatedStyle(() => ({
        backgroundColor: buttonBackgroundColor.value,
    }));

    const rightStyles = useAnimatedStyle(() => ({
        marginLeft: rightMarginLeft.value,
        paddingLeft: rightPaddingLeft.value,
        width: rightWidth.value,
    }));

    useEffect(() => {
        onSwitchPanel(isPanelFullScreen, 0);
    }, [onSwitchPanel, isPanelFullScreen, windowWidth]);

    const rightContentStyles = {
        width: LARGE_CAROUSEL_WIDTH * carouselScale,
        top: -((1 - carouselScale) * 300),
    };

    return (
        <>
            <View style={[styles.container, isButtonPositionLoading && { opacity: 0 }]}>
                <RandomLine
                    ref={backgroundLineRef}
                    style={{ position: "absolute", left: -5, top: 0 }}
                    width={windowWidth * 1.2}
                    height={windowHeight + insets.top}
                    points={8}
                    color={[colors.quaternary, colors.secondary]}
                />
                <Animated.View style={[styles.leftContainer, leftStyles]}>
                    <LogoImage
                        height={83.56}
                        width={114}
                        style={{ position: "absolute", top: 24 }}
                    />
                    <View style={styles.leftContentContainer}>
                        <AnimatedMarkdownView
                            style={[
                                opacityTitleStyles,
                                {
                                    position: "absolute",
                                    top: -65,
                                    width: 400,
                                    height: 64,
                                    alignItems: "center",
                                    justifyContent: "center",
                                },
                            ]}
                            markdownStyle={{
                                textgroup: { textAlign: "center", color: colors.secondary },
                            }}
                            markdown={title}
                        />
                        <View
                            style={{
                                width: LARGE_CAROUSEL_WIDTH * carouselScale,
                                height: LARGE_CAROUSEL_HEIGHT * carouselScale,
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                borderRadius: BUTTON_BORDER_RADIUS * carouselScale,
                                position: "relative",
                                backgroundColor: colors.background,
                            }}
                        >
                            <MaskedView
                                maskElement={
                                    <View
                                        style={{
                                            width: LARGE_CAROUSEL_WIDTH * carouselScale,
                                            height: LARGE_CAROUSEL_HEIGHT * carouselScale,
                                            borderRadius: BUTTON_BORDER_RADIUS * carouselScale,
                                            borderWidth: LARGE_CAROUSEL_BORDER_WIDTH,
                                        }}
                                    />
                                }
                            >
                                <LinearGradient
                                    start={{ x: 0.55, y: 0.55 }}
                                    end={{ x: 1, y: 0.4 }}
                                    locations={[0.5, 0.8]}
                                    colors={[colors.lighterGrey, colors.quaternary]}
                                    style={StyleSheet.absoluteFill}
                                />
                            </MaskedView>
                            <LargeWelcomeCarousel
                                onCarouselChange={onCarouselChange}
                                onButtonLayout={setButtonPosition}
                                autoSlider={!isInitialLogin}
                                carouselScale={carouselScale}
                            />
                        </View>
                    </View>
                </Animated.View>

                <Animated.View style={[styles.rightContainer, rightStyles]}>
                    <BlurView blurRadius={6} />
                    <LinearGradient
                        start={{ x: 0.55, y: 0.55 }}
                        end={{ x: 1, y: 0.4 }}
                        locations={[0.5, 0.8]}
                        colors={[colors.brightGrey, colors.quaternary]}
                        style={[StyleSheet.absoluteFill, { opacity: 0.8 }]}
                    />
                    {!phoneNumberConfirmResultValue ? (
                        <VStack
                            gap={1}
                            style={[rightContentStyles, !buttonPosition && { opacity: 0.5 }]}
                        >
                            <VStack gap="1/3">
                                <Animated.Text style={[titleStyle, styles.rightTitle]}>
                                    {titleText}
                                </Animated.Text>
                                <Animated.Text style={[descriptionStyle, styles.rightDescription]}>
                                    {descriptionText}
                                </Animated.Text>
                            </VStack>
                            <VStack gap="2/3">
                                <PhoneField
                                    ref={phoneInputRef}
                                    value={phoneNumber}
                                    onPhoneNumberChanged={onPhoneNumberChanged}
                                    autoFocus
                                    placeholder={i18n.t(
                                        "screens.auth.phone_number.phone_placeholder",
                                    )}
                                    isValid={isPhoneNumberValid}
                                    style={styles.rightPhoneField}
                                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                    onSubmitEditing={validatePhoneNumber}
                                />
                                <AnimatedButton
                                    style={buttonStyle}
                                    label={buttonText}
                                    size="large"
                                    onPress={validatePhoneNumber}
                                    processing={isProcessing}
                                />
                            </VStack>
                        </VStack>
                    ) : (
                        <VStack gap={1} style={rightContentStyles}>
                            <VStack gap="1/3" alignment="center">
                                <Text style={styles.rightTitle}>
                                    {i18n.t("screens.auth.confirmation_code.title")}
                                </Text>
                                <Text style={styles.rightDescription}>
                                    {i18n.t("screens.auth.confirmation_code.description", {
                                        phoneNumber,
                                    })}
                                </Text>
                            </VStack>
                            <VStack gap="2/3">
                                <CodeField
                                    value={codeValue}
                                    setValue={setCodeValue}
                                    cellCount={6}
                                    autoFocus
                                    noBorders
                                    cellStyle={
                                        isLoading ? { backgroundColor: colors.backdrop } : {}
                                    }
                                />
                                <Loading
                                    color={colors.onPrimary}
                                    style={{ opacity: isLoading ? 1 : 0 }}
                                />
                            </VStack>
                        </VStack>
                    )}
                    <Text
                        style={[
                            commonStyle.screenErrorMessage,
                            { color: message?.color, opacity: message ? 1 : 0 },
                        ]}
                    >
                        {message?.text || " "}
                    </Text>
                </Animated.View>
                {!isInitialLogin && (
                    <VStack gap="2/3" style={styles.ourPartnersContainer}>
                        <BodySmallText>
                            {i18n.t("screens.auth.presentation_carousel.our_partners")}
                        </BodySmallText>
                        <View style={styles.ourPartnersLogos}>
                            <AutoSizeImage
                                style={{ transform: [{ translateY: -3.5 }] }}
                                source={require("../../../../assets/images/centre_patronal.png")}
                                fixedHeight={PARTNER_LOGO_HEIGHT}
                            />
                            <AutoSizeImage
                                source={require("../../../../assets/images/suva.png")}
                                fixedHeight={PARTNER_LOGO_HEIGHT}
                                scale={0.6}
                            />
                            <AutoSizeImage
                                source={require("../../../../assets/images/groupe_mutuel.png")}
                                fixedHeight={PARTNER_LOGO_HEIGHT}
                            />
                            <AutoSizeImage
                                source={require("../../../../assets/images/gastrosocial.png")}
                                fixedHeight={PARTNER_LOGO_HEIGHT}
                                scale={0.7}
                            />
                        </View>
                    </VStack>
                )}
                <View style={styles.qrLineContainer}>
                    <View style={styles.qrCodeWrapper}>
                        <QRCode
                            size={53}
                            bgColor="transparent"
                            value="https://app.liftapp.ch/invite"
                        />
                    </View>
                    <MarkdownView
                        style={{ flex: 1, transform: [{ translateY: 4 }] }}
                        markdownStyle={{ paragraph: styles.paragraph, strong: styles.strong }}
                        markdown={i18n.t("screens.auth.welcome.scan_qr_code")}
                    />
                </View>
                {!phoneNumberConfirmResultValue && (
                    <Pressable
                        style={styles.topRightPressable}
                        onPress={() => {
                            if (isInitialLogin) {
                                navigation.replace("Welcome");
                            } else {
                                setTopRightText(loginTexts[+!isLogin].topRight);
                                setTitleText(loginTexts[+!isLogin].title);
                                setDescriptionText(loginTexts[+!isLogin].description);
                                setButtonText(loginTexts[+!isLogin].button);
                                setIsLogin(!isLogin);
                            }
                        }}
                    >
                        <Animated.Text
                            style={[
                                topRightStyle,
                                styles.bodySmallText,
                                { color: colors.secondary },
                            ]}
                        >
                            {topRightText}
                        </Animated.Text>
                    </Pressable>
                )}
                {Platform.OS === "web" && <div id={CAPTCHA_ID} />}
            </View>
            {buttonPosition && !isButtonPositionLoading && (
                <AnimatedPressable
                    style={[
                        buttonStyles,
                        buttonBackgroundStyles,
                        {
                            pointerEvents: !isPanelFullScreen ? "none" : undefined,
                            position: "absolute",
                            left: buttonPosition.x,
                            top:
                                buttonPosition.y +
                                buttonPosition.height -
                                CAROUSEL_BOTTOM_HEIGHT_BASE * carouselScale,
                            width: buttonPosition.width,
                            height: CAROUSEL_BOTTOM_HEIGHT_BASE * carouselScale,
                            borderBottomLeftRadius: BUTTON_BORDER_RADIUS * carouselScale,
                            borderBottomRightRadius: BUTTON_BORDER_RADIUS * carouselScale,
                            alignItems: "center",
                            justifyContent: "center",
                        },
                    ]}
                    onPress={() => {
                        if (isInitialLogin) {
                            if (Platform.OS === "web") {
                                window.history.back();
                            } else {
                                navigation.goBack();
                            }
                        } else {
                            onSwitchPanel(false);
                            onCarouselChange(3);
                        }
                    }}
                >
                    <MaskedView
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        ref={maskRef}
                        androidRenderingMode="software"
                        style={{ position: "absolute", left: 0, top: 0 }}
                        maskElement={
                            <Animated.View
                                style={[
                                    buttonStyles,
                                    {
                                        width: LARGE_CAROUSEL_WIDTH * carouselScale,
                                        height: CAROUSEL_BOTTOM_HEIGHT_BASE * carouselScale,
                                        borderBottomLeftRadius:
                                            BUTTON_BORDER_RADIUS * carouselScale,
                                        borderBottomRightRadius:
                                            BUTTON_BORDER_RADIUS * carouselScale,
                                        borderWidth: 10,
                                    },
                                ]}
                            />
                        }
                    >
                        <LinearGradient
                            start={{ x: 0.55, y: 0.55 }}
                            end={{ x: 1, y: 0.4 }}
                            locations={[0.5, 0.8]}
                            colors={[colors.lighterGrey, colors.quaternary]}
                            style={StyleSheet.absoluteFill}
                        />
                    </MaskedView>
                    <AnimatedIcon
                        name="chevron-left"
                        color={colors.secondary}
                        size={24 * carouselScale}
                        style={[
                            buttonContentStyles,
                            {
                                position: "absolute",
                                left: 30 * carouselScale,
                                top: 30 * carouselScale,
                            },
                        ]}
                    />
                    <Animated.Text style={[buttonContentStyles, styles.bodySmallText]}>
                        {i18n.t("screens.auth.presentation_carousel.go_back")}
                    </Animated.Text>
                </AnimatedPressable>
            )}
        </>
    );
};

const useStyles = () => {
    const { gridSize, marginSize, colors, fonts } = useTheme();

    return StyleSheet.create({
        container: {
            flexDirection: "row",
            flex: 1,
            overflow: "hidden",
        },
        leftContainer: {
            width: "50%",
            height: "100%",
            position: "absolute",
            alignItems: "center",
            justifyContent: "center",
            // justifyContent: "space-between",
            padding: gridSize,
        },
        leftContentContainer: {
            alignItems: "center",
            justifyContent: "center",
            // transform: [{ translateY: -80 }],
        },
        ourPartnersContainer: {
            position: "absolute",
            bottom: 32,
            left: 32,
            flexDirection: "column",
            width: "100%",
        },
        ourPartnersLogos: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: 510,
            paddingHorizontal: gridSize / 2,
        },
        qrLineContainer: {
            position: "absolute",
            left: "52%",
            bottom: 32,
            flexDirection: "row",
            alignItems: "flex-end",
        },
        qrCodeWrapper: {
            marginRight: marginSize / 2,
        },
        paragraph: {
            ...fonts.bodySmall,
            fontSize: 13,
        },
        strong: {
            ...fonts.bold,
            fontSize: 13,
        },
        bodySmallText: {
            ...fonts.bodySmall,
            color: colors.primary,
        },
        topRightPressable: {
            position: "absolute",
            top: 24,
            right: 24,
        },
        rightContainer: {
            // flex: 1,
            // backgroundColor: colors.secondary,
            padding: gridSize,
            // paddingLeft: gridSize * 4,
            // right: 0,
            // alignItems: "center",
            justifyContent: "center",
        },
        rightContent: {
            width: LARGE_CAROUSEL_WIDTH,
        },
        rightTitle: {
            ...fonts.titleLarge,
            // color: colors.background,
        },
        rightDescription: {
            ...fonts.bodyMedium,
            // color: colors.background,
        },
        rightPhoneField: {
            backgroundColor: colors.background,
            borderRadius: 100,
            color: colors.onPrimary,
            borderColor: "transparent",
        },
    });
};
